/**
 * UserManagement — superadmin-only user administration.
 * Create/role/enable/disable/reset-password/delete users. No email; new users
 * get a temporary password and must change it on first login.
 */

import { useState, useEffect, useCallback } from 'react';
import { LoadingState } from '../../../components/LoadingState';
import { ErrorState } from '../../../components/ErrorState';
import { ActivityTab } from './CTIAuditLog';
import {
  listUsers,
  createUser,
  setUserRole,
  setUserEnabled,
  resetUserPassword,
  deleteUser,
  USER_ROLES,
  type ManagedUser,
  type UserRole,
} from '../../../api/userAdmin';

function friendly(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  if (/403|superadmin/i.test(msg)) return 'You need superadmin access to manage users.';
  if (/409|already exists/i.test(msg)) return 'A user with that email already exists.';
  if (/404|no user/i.test(msg)) return 'That user no longer exists.';
  if (/network/i.test(msg)) return 'Cannot reach the server. Check your connection.';
  return 'Something went wrong. Please try again.';
}

function UsersTab() {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<UserRole>('reader');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await listUsers());
    } catch (e) {
      setError(friendly(e));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const run = async (fn: () => Promise<void>, ok: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(ok);
      await fetchData();
    } catch (e) {
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  };

  const onCreate = () => {
    const email = newEmail.trim();
    if (!email || !email.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    run(
      () => createUser(email, newRole),
      `Created ${email} (${newRole}). They must change the temporary password on first login.`,
    ).then(() => {
      setNewEmail('');
      setNewRole('reader');
    });
  };

  const onDelete = (u: ManagedUser) => {
    const who = u.email || u.username;
    if (!window.confirm(`Permanently delete ${who}? This cannot be undone.`)) return;
    run(() => deleteUser(who), `Deleted ${who}.`);
  };

  if (loading) return <LoadingState message="Loading users..." />;

  return (
    <div className="space-y-8">
      <p className="text-body text-slate-muted">
        Create and manage users (superadmin access). New users get a temporary
        password and must change it on first login.
      </p>

      {notice && (
        <div className="glass-card border-2" style={{ borderColor: '#22c55e' }}>
          <p className="text-body" style={{ color: '#16a34a' }}>{notice}</p>
        </div>
      )}
      {error && <ErrorState message={error} onRetry={fetchData} />}

      {/* Create user */}
      <div className="glass-card space-y-4">
        <h2 className="text-body font-semibold text-slate-text">Add User</h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="user@example.com"
            className="flex-1 px-4 py-3 text-body rounded-btn border-2 focus:outline-none focus:border-primary-blue bg-white"
            style={{ borderColor: 'var(--color-card-border)' }}
          />
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as UserRole)}
            className="px-4 py-3 text-body rounded-btn border-2 focus:outline-none focus:border-primary-blue bg-white"
            style={{ borderColor: 'var(--color-card-border)' }}
          >
            {USER_ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button className="btn-primary" onClick={onCreate} disabled={busy}>
            Add User
          </button>
        </div>
      </div>

      {/* Users table */}
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-body">
          <thead>
            <tr className="text-left border-b-2" style={{ borderColor: 'var(--color-card-border)' }}>
              <th className="py-3 pr-4">Email</th>
              <th className="py-3 pr-4">Role</th>
              <th className="py-3 pr-4">Status</th>
              <th className="py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.username} className="border-b" style={{ borderColor: 'var(--color-card-border)' }}>
                <td className="py-3 pr-4 text-slate-text">{u.email || u.username}</td>
                <td className="py-3 pr-4">
                  <select
                    value={USER_ROLES.includes(u.role as UserRole) ? u.role : 'reader'}
                    disabled={busy}
                    onChange={(e) => run(
                      () => setUserRole(u.email || u.username, e.target.value as UserRole),
                      `Updated role for ${u.email} to ${e.target.value}.`,
                    )}
                    className="px-2 py-1 rounded border-2 bg-white"
                    style={{ borderColor: 'var(--color-card-border)' }}
                  >
                    {USER_ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </td>
                <td className="py-3 pr-4 text-slate-muted">
                  {!u.enabled ? 'disabled' : (u.status || 'active')}
                </td>
                <td className="py-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      className="px-3 py-1.5 rounded border-2 text-[14px]"
                      style={{ borderColor: 'var(--color-card-border)' }}
                      disabled={busy}
                      onClick={() => run(
                        () => setUserEnabled(u.email || u.username, !u.enabled),
                        `${u.enabled ? 'Disabled' : 'Enabled'} ${u.email}.`,
                      )}
                    >
                      {u.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      className="px-3 py-1.5 rounded border-2 text-[14px]"
                      style={{ borderColor: 'var(--color-card-border)' }}
                      disabled={busy}
                      onClick={() => run(
                        () => resetUserPassword(u.email || u.username),
                        `Reset temporary password for ${u.email}.`,
                      )}
                    >
                      Reset Password
                    </button>
                    <button
                      className="px-3 py-1.5 rounded border-2 text-[14px]"
                      style={{ borderColor: '#ef4444', color: '#ef4444' }}
                      disabled={busy}
                      onClick={() => onDelete(u)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type Tab = 'users' | 'activity';

/**
 * UserManagement — superadmin-only page with two tabs:
 *   - Users: create/manage users
 *   - Activity: user-activity audit log (actions + source IP)
 */
export function UserManagement() {
  const [tab, setTab] = useState<Tab>('users');

  const tabBtn = (id: Tab, label: string) => (
    <button
      onClick={() => setTab(id)}
      className={`px-4 py-2 text-body font-semibold border-b-2 transition-colors ${
        tab === id ? 'text-slate-text' : 'text-slate-muted border-transparent'
      }`}
      style={tab === id ? { borderColor: 'var(--color-primary-teal)', color: 'var(--color-primary-teal)' } : undefined}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-heading-lg font-bold text-slate-text">User Management</h1>
        <p className="text-body text-slate-muted mt-1">
          Manage users and review user activity (superadmin access).
        </p>
      </div>

      <div className="flex gap-2 border-b-2" style={{ borderColor: 'var(--color-card-border)' }}>
        {tabBtn('users', 'Users')}
        {tabBtn('activity', 'Activity')}
      </div>

      {tab === 'users' ? <UsersTab /> : <ActivityTab />}
    </div>
  );
}
